import colorSet from "@/src/styles/color";
import typo from "@/src/styles/typography";
import { ButtonHTMLAttributes, DetailedHTMLProps, forwardRef } from "react";
import { css, styled } from "styled-components";
import Loader from "../Loader";

export type ButtonColorType = "blue" | "red" | "black";
export type ButtonSizeType = 40 | 32 | 24;
export type ButtonGradeType = "primary" | "secondary" | "tertiary" | "ghost";
export type ButtonVariantType = "default" | "landing";

export interface ButtonProps
  extends DetailedHTMLProps<
    ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > {
  buttonColor?: ButtonColorType;
  buttonSize?: ButtonSizeType;
  buttonGrade?: ButtonGradeType;
  buttonVariant?: ButtonVariantType;
  isIconOnly?: boolean;
  isLoading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      buttonColor = "blue",
      buttonSize = 40,
      buttonGrade = "primary",
      buttonVariant = "default",
      isIconOnly = false,
      isLoading = false,
      children,
      onClick,
      ...restProps
    },
    ref,
  ) => {
    return (
      <StyledBasicButton
        ref={ref}
        type="button"
        $buttonColor={buttonColor}
        $buttonSize={buttonSize}
        $buttonGrade={buttonGrade}
        $buttonVariant={buttonVariant}
        $isIconOnly={isIconOnly}
        $isLoading={isLoading}
        data-loading={isLoading}
        aria-disabled={restProps.disabled}
        data-disabled={restProps.disabled}
        onClick={(e) => {
          if (!isLoading) {
            onClick?.(e);
          }
        }}
        {...restProps}
      >
        {isLoading ? (
          <Loader
            size={buttonSize === 40 ? 20 : buttonSize === 32 ? 18 : 14}
            tickColor={
              buttonGrade === "primary" ? colorSet.white : colorSet.blue4
            }
            trackColor={
              buttonGrade === "primary"
                ? "rgba(255, 255, 255, 0.5)"
                : "rgba(22, 109, 215, 0.2);"
            }
          />
        ) : (
          children
        )}
      </StyledBasicButton>
    );
  },
);

export default Button;

export const getPrimaryButtonColor = (color: ButtonColorType) => {
  switch (color) {
    case "blue":
      return css`
        color: ${colorSet.white};
        background: ${colorSet.indigo};
        box-shadow:
          0px 1px 2px 0px rgba(5, 29, 57, 0.1),
          0px 0px 0px 0.5px rgba(23, 98, 195, 0.2);

        &:hover {
          box-shadow:
            0px 2px 4px 0px rgba(5, 29, 57, 0.2),
            0px 0px 0px 0.5px rgba(23, 98, 195, 0.2);
          transform: translateY(-2px);
        }
        &:active {
          box-shadow:
            0px 2px 4px 0px rgba(5, 29, 57, 0.2),
            0px 0px 0px 2px rgba(173, 204, 243, 0.6),
            0px 0px 0px 1px rgba(23, 98, 195, 0.6);
        }
        &:disabled {
          background: ${colorSet.gray7};
          color: ${colorSet.white};
          box-shadow: 0px 1px 2px 0px rgba(5, 29, 57, 0.1);
        }
      `;
    case "red":
      return css`
        color: ${colorSet.white};
        background: ${colorSet.red2};
        box-shadow:
          0px 1px 2px 0px rgba(5, 29, 57, 0.1),
          0px 0px 0px 0.5px rgba(214, 76, 76, 0.2);
        &:hover {
          box-shadow:
            0px 2px 4px 0px rgba(5, 29, 57, 0.2),
            0px 0px 0px 0.5px rgba(214, 76, 76, 0.2);
          transform: translateY(-2px);
        }
        &:active {
          box-shadow:
            0px 2px 4px 0px rgba(5, 29, 57, 0.2),
            0px 0px 0px 2px rgba(214, 76, 76, 0.2),
            0px 0px 0px 1px #d64c4c;
        }
        &:disabled {
          background: ${colorSet.red6};
          box-shadow:
            0px 1px 2px 0px rgba(5, 29, 57, 0.1),
            0px 0px 0px 0.5px rgba(214, 76, 76, 0.2);
        }
      `;
  }
};

export const getSecondaryButtonColor = (color: ButtonColorType) => {
  switch (color) {
    case "blue":
      return css`
        color: ${colorSet.indigo};
        background: ${colorSet.blue10};
        box-shadow:
          0px 1px 2px 0px rgba(5, 29, 57, 0.1),
          0px 0px 0px 0.5px rgba(23, 98, 195, 0.2);
        &:hover {
          box-shadow:
            0px 2px 4px 0px rgba(5, 29, 57, 0.2),
            0px 0px 0px 0.5px rgba(23, 98, 195, 0.2);
          transform: translateY(-2px);
        }
        &:active {
          box-shadow:
            0px 2px 4px 0px rgba(5, 29, 57, 0.2),
            0px 0px 0px 2px rgba(173, 204, 243, 0.6),
            0px 0px 0px 1px rgba(23, 98, 195, 0.6);
        }
        &:disabled {
          background: ${colorSet.gray11};
          color: ${colorSet.gray8};
          box-shadow: 0px 1px 2px rgba(5, 29, 57, 0.1);
        }
      `;
    case "red":
      return css`
        color: ${colorSet.red2};
        background: ${colorSet.red8};
        box-shadow:
          0px 0px 0px 0.5px rgba(214, 76, 76, 0.2),
          0px 1px 2px rgba(5, 29, 57, 0.1);
        &:hover {
          box-shadow:
            0px 0px 0px 0.5px rgba(160, 164, 171, 0.2),
            0px 2px 4px rgba(5, 29, 57, 0.2);
          transform: translateY(-2px);
        }
        &:active {
          box-shadow:
            0px 0px 0px 1px #d64c4c,
            0px 0px 0px 2px rgba(214, 76, 76, 0.2),
            0px 2px 4px rgba(5, 29, 57, 0.2);
        }
        &:disabled {
          color: ${colorSet.red6};
          background: ${colorSet.red8};
          box-shadow: 0px 1px 2px rgba(5, 29, 57, 0.1);
        }
      `;
  }
};

export const getTertiaryButtonColor = (color: ButtonColorType) => {
  switch (color) {
    case "black":
      return css`
        color: ${colorSet.gray2};
        background: ${colorSet.white};
        box-shadow:
          0px 0px 0px 0.5px rgba(160, 164, 171, 0.2),
          0px 1px 2px rgba(5, 29, 57, 0.1);
        &:hover {
          box-shadow:
            0px 0px 0px 0.5px rgba(160, 164, 171, 0.2),
            0px 2px 4px rgba(5, 29, 57, 0.2);
          transform: translateY(-2px);
        }
        &:active {
          box-shadow:
            0px 0px 0px 1px rgba(5, 29, 57, 0.6),
            0px 0px 0px 2px rgba(5, 29, 57, 0.2),
            0px 2px 4px rgba(5, 29, 57, 0.2);
        }
        &:disabled {
          background: ${colorSet.white};
          color: ${colorSet.gray8};
          box-shadow:
            0px 0px 0px 0.5px rgba(160, 164, 171, 0.2),
            0px 1px 2px rgba(5, 29, 57, 0.1);
        }
      `;
    case "red":
      return css`
        color: ${colorSet.red2};
        background: ${colorSet.white};
        box-shadow:
          0px 0px 0px 0.5px rgba(160, 164, 171, 0.2),
          0px 1px 2px rgba(5, 29, 57, 0.1);
        &:hover {
          box-shadow:
            0px 0px 0px 0.5px rgba(160, 164, 171, 0.2),
            0px 2px 4px rgba(5, 29, 57, 0.2);
          transform: translateY(-2px);
        }
        &:active {
          box-shadow:
            0px 0px 0px 1px #d64c4c,
            0px 0px 0px 2px rgba(214, 76, 76, 0.2),
            0px 2px 4px rgba(5, 29, 57, 0.2);
        }
        &:disabled {
          background: ${colorSet.white};
          color: ${colorSet.red6};
          box-shadow:
            0px 0px 0px 0.5px rgba(214, 76, 76, 0.2),
            0px 1px 2px rgba(5, 29, 57, 0.1);
        }
      `;
  }
};

export const getButtonPadding = (size: ButtonSizeType) => {
  switch (size) {
    case 40:
      return css`
        padding: 10px 16px;
        ${typo.btn3m}
      `;
    case 32:
      return css`
        padding: 6px 12px;
        ${typo.btn3m}
      `;
    case 24:
      return css`
        padding: 3px 8px;
        ${typo.btn4m}
      `;
  }
};

export const getLandingVariantStyle = () => css`
  padding: 15px 25px;
  ${typo.btn2m}
  color: ${colorSet.white};
  background: ${colorSet.blue4};
  border: 1px solid ${colorSet.blue4};

  &:hover {
    background: ${colorSet.blue3};
    border-color: ${colorSet.blue3};
    box-shadow: none;
    transform: none;
  }

  &:active {
    background: ${colorSet.blue2};
    border-color: ${colorSet.blue2};
    box-shadow: none;
  }

  &:disabled {
    color: ${colorSet.white};
    background: ${colorSet.gray7};
    border-color: ${colorSet.gray7};
    box-shadow: none;
  }
`;

export const getGhostButtonColor = (color: ButtonColorType) => {
  switch (color) {
    case "blue":
      return css`
        color: ${colorSet.blue4};
        background: transparent;
        border: none;

        &:hover {
          color: ${colorSet.blue5};
        }

        &:active {
          color: ${colorSet.blue6};
        }

        &:disabled {
          color: ${colorSet.gray8};
        }
      `;
    case "red":
      return css`
        color: ${colorSet.red4};
        background: transparent;
        border: none;

        &:hover {
          color: ${colorSet.red5};
        }

        &:active {
          color: ${colorSet.red6};
        }

        &:disabled {
          color: ${colorSet.gray8};
        }
      `;
    case "black":
      return css`
        color: ${colorSet.gray2};
        background: transparent;
        border: none;

        &:hover {
          color: ${colorSet.gray3};
        }

        &:active {
          color: ${colorSet.gray4};
        }

        &:disabled {
          color: ${colorSet.gray8};
        }
      `;
    default:
      return css`
        color: ${colorSet.gray2};
        background: transparent;
        border: none;

        &:hover {
          color: ${colorSet.gray3};
        }

        &:active {
          color: ${colorSet.gray4};
        }

        &:disabled {
          color: ${colorSet.gray8};
        }
      `;
  }
};

const StyledBasicButton = styled.button<{
  $buttonColor: ButtonColorType;
  $buttonSize: ButtonSizeType;
  $buttonGrade: ButtonGradeType;
  $buttonVariant: ButtonVariantType;
  $isIconOnly: boolean;
  $isLoading: boolean;
}>`
  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  &:focus {
    outline-offset: 1px;
    outline: 2px solid ${colorSet.blue5};
  }
  ${({ $buttonSize, $buttonVariant, $isIconOnly }) =>
    $isIconOnly
      ? null
      : $buttonVariant === "landing"
        ? getLandingVariantStyle()
        : getButtonPadding($buttonSize)};
  ${({ $buttonGrade, $buttonColor, $buttonVariant }) =>
    $buttonVariant === "landing"
      ? null
      : $buttonGrade === "primary"
        ? getPrimaryButtonColor($buttonColor)
        : $buttonGrade === "secondary"
          ? getSecondaryButtonColor($buttonColor)
          : $buttonGrade === "tertiary"
            ? getTertiaryButtonColor($buttonColor)
            : $buttonGrade === "ghost"
              ? getGhostButtonColor($buttonColor)
              : null};
  ${({ $isLoading }) =>
    $isLoading &&
    css`
      cursor: wait;
      display: flex;
      align-items: center;
      justify-content: center;
    `};

  &:disabled {
    transform: none;
    cursor: not-allowed;
  }
`;
